/* Shared "now playing" state for chat voice and video-note players.
 * Each player registers a handle; only one plays at a time, and the chat
 * playback bar drives whichever handle is active. */
import { useSyncExternalStore } from "react";

const handles = new Set();
const listeners = new Set();
let active = null;
let version = 0;
/* A playing video note that scrolled out of view continues in the floating
 * mini player: { source, src, start: { time, playing, rate, volume }, handle }. */
let floating = null;
let floatingId = 0;

const emit = () => {
	version += 1;
	listeners.forEach((listener) => listener());
};

export const subscribePlayback = (listener) => {
	listeners.add(listener);
	return () => listeners.delete(listener);
};

export const registerPlayback = (handle) => {
	handles.add(handle);
	return () => {
		handles.delete(handle);
		if (active === handle) { active = null; emit(); }
	};
};

/* Called by a player when it starts; pauses whatever else was playing. */
export const claimPlayback = (handle) => {
	if (active && active !== handle && active.isPlaying()) active.pause();
	active = handle;
	emit();
};

export const notifyPlayback = emit;

export const getActivePlayback = () => active;

export const closePlayback = () => {
	if (!active) return;
	active.pause();
	active = null;
	emit();
};

export const getFloating = () => floating;

export const startFloating = (value) => {
	floatingId += 1;
	floating = { ...value, id: floatingId };
	emit();
};

/* The floating player registers its own handle once mounted. */
export const attachFloatingHandle = (value, handle) => {
	if (floating === value) floating.handle = handle;
};

/* Ends the floating session and returns where its playback got to. */
export const endFloating = () => {
	if (!floating) return null;
	const { handle, start } = floating;
	const state = handle
		? { time: handle.getTime(), playing: handle.isPlaying(), rate: handle.getRate(), volume: handle.getVolume() }
		: start;
	floating = null;
	emit();
	return state;
};

/* Mounted players in on-screen order, so prev/next follow the conversation.
 * A floated-away source is skipped: the floating handle stands in its place. */
const orderedHandles = () => [...handles]
	.filter((handle) => handle !== floating?.source && handle.getNode?.()?.isConnected)
	.sort((a, b) => (a.getNode().compareDocumentPosition(b.getNode()) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

export const getSiblingPlayback = (offset) => {
	if (!active) return null;
	const list = orderedHandles();
	const index = list.indexOf(active);
	return index < 0 ? null : list[index + offset] || null;
};

export const playSibling = (offset) => {
	const sibling = getSiblingPlayback(offset);
	if (!sibling) return false;
	sibling.seek(0);
	sibling.play();
	return true;
};

/* Players call this on natural end so consecutive voice messages play through. */
export const finishPlayback = (handle) => {
	if (active !== handle) return;
	if (!playSibling(1)) emit();
};

export const usePlaybackVersion = () => useSyncExternalStore(subscribePlayback, () => version);

/* Re-renders only when the selected primitive changes, not on every time update. */
export const usePlaybackState = (selector) => useSyncExternalStore(subscribePlayback, selector);

export const formatPlaybackTime = (seconds) => {
	const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
	return `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
};
